import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Form,
  TvAlert,
  TvSmall,
  TvButton,
  TvDiv,
  TvLabel,
} from "@trustvid/design-system";
import { actorSendOTP, verify_account_details } from "../../../services/auth";
import { toast } from "react-toastify";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import FullScreenLoader from "../../../components/CommonForBoth/loader";
import useActorInviteBranding from "../../../hooks/useActorInviteBranding";
import AuthSplitLayout from "./AuthSplitLayout";
import {
  ACTOR_LOGIN_PAGE_COPY,
  AUTH_FIELD_LABEL_CLASS,
  buildActorLoginWelcomeLine,
} from "./loginPageContent";

const ActorLogin = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [loading, setloading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { account } = useActorInviteBranding(token, { applyTheme: false });

  const showSubmitError = (message) => {
    setSubmitError(message);
    toast.error(message);
  };

  document.title = "Actor Login";

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      mobile_no: "",
      token: token,
    },
    validationSchema: Yup.object({
      mobile_no: Yup.string().required("Please Enter Your Mobile Number"),
    }),
    onSubmit: async (values) => {
      setSubmitError("");
      setloading(true);
      try {
        const parsedNumber = parsePhoneNumber(values.mobile_no);
        const nationalNumber = parsedNumber?.nationalNumber;

        if (!nationalNumber) {
          showSubmitError(ACTOR_LOGIN_PAGE_COPY.invalidMobileMessage);
          setloading(false);
          return;
        }

        localStorage.setItem("actorMobileNo", nationalNumber);

        const response = await verify_account_details({
          mobile_no: nationalNumber,
          token: token,
        });

        if (response?.error) {
          showSubmitError(response.error || ACTOR_LOGIN_PAGE_COPY.verifyFailedMessage);
          setloading(false);
          return;
        }

        const responseOtp = await actorSendOTP({
          mobile_no: nationalNumber,
          token: token,
        });

        if (responseOtp?.error) {
          showSubmitError(responseOtp.error || ACTOR_LOGIN_PAGE_COPY.otpFailedMessage);
          setloading(false);
          return;
        }

        setloading(false);
        navigate(`/actors/onboarding/invite/verify/${token}`);
        toast.success("Details verified, OTP sent successfully!");
      } catch (err) {
        setloading(false);
        showSubmitError(ACTOR_LOGIN_PAGE_COPY.unexpectedErrorMessage);
        formik.resetForm();
      }
    },
  });

  return (
    <>
      <FullScreenLoader loading={loading} setLoading={setloading} />
      <AuthSplitLayout
        pageTestId="actor-login-page"
        cardTestId="actor-login-form-card"
        cardTitle={ACTOR_LOGIN_PAGE_COPY.pageTitle}
        welcomeHeading={ACTOR_LOGIN_PAGE_COPY.pageTitle}
        welcomeHeadingTestId="actor-login-card-title"
        cardSubtitle={`${buildActorLoginWelcomeLine(account?.name)} ${ACTOR_LOGIN_PAGE_COPY.cardSubtitle}`}
        featurePanelHeading={ACTOR_LOGIN_PAGE_COPY.featurePanelHeading}
        showCardHeading={false}
      >
        <Form
          className="form-horizontal tv-login-page__form"
          data-testid="actor-login-form"
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
            return false;
          }}
        >
          {submitError ? (
            <TvAlert color="danger" className="mb-3" data-testid="actor-login-error" fade={false}>
              {submitError}
            </TvAlert>
          ) : null}

          <TvDiv className="mb-4">
            <TvLabel htmlFor="actor-login-mobile" className={AUTH_FIELD_LABEL_CLASS}>
              {ACTOR_LOGIN_PAGE_COPY.mobileLabel}
            </TvLabel>
            <PhoneInput
              id="actor-login-mobile"
              international
              defaultCountry="IN"
              value={formik.values.mobile_no}
              onChange={(value) => {
                setSubmitError("");
                formik.setFieldValue("mobile_no", value);
              }}
              placeholder={ACTOR_LOGIN_PAGE_COPY.mobilePlaceholder}
              aria-label={ACTOR_LOGIN_PAGE_COPY.mobileLabel}
              className={`form-control d-flex loginmobile tv-login-page__field ${
                formik.touched.mobile_no && formik.errors.mobile_no ? "is-invalid" : ""
              }`}
            />
            {formik.touched.mobile_no && formik.errors.mobile_no ? (
              <TvSmall className="text-danger d-block mt-1">{formik.errors.mobile_no}</TvSmall>
            ) : null}
          </TvDiv>
          <TvDiv className="d-grid">
            <TvButton color="primary" className="tv-login-page__submit" type="submit">
              {ACTOR_LOGIN_PAGE_COPY.sendOtpButton}
            </TvButton>
          </TvDiv>
        </Form>
      </AuthSplitLayout>
    </>
  );
};

export default ActorLogin;
